import { useState, useCallback } from 'react';
import { X, Upload, FileText, Music, AlertTriangle } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useScoreStore } from '@/stores';
import { uploadScore, classifyPDF, ApiError } from '@/services/api';
import type { Score, PDFClassification } from '@/types';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (scoreId: string) => void;
}

type UploadStep = 'select' | 'classifying' | 'confirm' | 'uploading' | 'complete';

export function UploadModal({ isOpen, onClose, onSuccess }: UploadModalProps) {
  const [step, setStep] = useState<UploadStep>('select');
  const [file, setFile] = useState<File | null>(null);
  const [classification, setClassification] = useState<PDFClassification | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorTraceId, setErrorTraceId] = useState<string | undefined>(undefined);
  const [progress, setProgress] = useState(0);
  const [isScan, setIsScan] = useState(false);

  const addScore = useScoreStore((state) => state.addScore);

  const resetState = useCallback(() => {
    setStep('select');
    setFile(null);
    setClassification(null);
    setError(null);
    setErrorTraceId(undefined);
    setProgress(0);
    setIsScan(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    setFile(selectedFile);
    setError(null);
    setErrorTraceId(undefined);

    const extension = selectedFile.name.split('.').pop()?.toLowerCase();

    // For MusicXML and MIDI, skip classification and go straight to upload
    if (
      extension === 'xml' ||
      extension === 'musicxml' ||
      extension === 'mxl' ||
      extension === 'mid' ||
      extension === 'midi'
    ) {
      setStep('uploading');
      await handleUpload(selectedFile, null);
      return;
    }

    // For PDF, classify first
    if (extension === 'pdf') {
      setStep('classifying');
      try {
        const result = await classifyPDF(selectedFile);
        setClassification(result);
        setStep('confirm');
      } catch (err) {
        console.error('[UploadModal/classifyPDF] Failed: PDF classification | reason=', err);
        setError(err instanceof Error ? err.message : 'Failed to analyze PDF');
        setErrorTraceId(err instanceof ApiError ? err.traceId : undefined);
        setStep('select');
      }
      return;
    }

    setError('Unsupported file type. Please upload PDF, MusicXML, or MIDI.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = async (uploadFile: File, pdfClass: PDFClassification | null) => {
    setStep('uploading');
    setProgress(0);
    setErrorTraceId(undefined);

    try {
      const extension = uploadFile.name.split('.').pop()?.toLowerCase();
      let sourceType: 'pdf' | 'musicxml' | 'midi' = 'pdf';
      if (extension === 'xml' || extension === 'musicxml' || extension === 'mxl') {
        sourceType = 'musicxml';
      } else if (extension === 'mid' || extension === 'midi') {
        sourceType = 'midi';
      }

      // Create initial score entry
      const scoreId = uuidv4();
      const score: Score = {
        id: scoreId,
        title: uploadFile.name.replace(/\.[^/.]+$/, ''),
        sourceType,
        status: 'uploading',
        pdfClassification: pdfClass || undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      addScore(score);

      const result = await uploadScore(uploadFile, (p) => setProgress(p), { isScan });

      // Update score with server-assigned ID and result
      // The server generates its own UUID, so we need to update our local ID to match
      useScoreStore.getState().updateScore(scoreId, {
        id: result.scoreId, // Use server's ID, not client-generated one
        status: result.status,
        musicXmlUrl: result.musicXmlUrl,
        timeline: result.timeline,
        traceId: result.traceId,
        updatedAt: new Date().toISOString(),
      });

      setStep('complete');

      // Auto-close and navigate to practice view after success
      setTimeout(() => {
        handleClose();
        onSuccess?.(result.scoreId);
      }, 1500);
    } catch (err) {
      console.error('[UploadModal/upload] Failed: score upload | reason=', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
      setErrorTraceId(err instanceof ApiError ? err.traceId : undefined);
      setStep('select');
    }
  };

  const handleConfirmUpload = () => {
    if (file && classification) {
      handleUpload(file, classification);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        handleFileSelect(droppedFile);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h2 className="text-lg font-semibold">Upload Score</h2>
          <button onClick={handleClose} className="p-1 hover:bg-gray-700 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'select' && (
            <div
              className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-gray-500 transition-colors cursor-pointer"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg mb-2">Drop your score here</p>
              <p className="text-sm text-gray-400 mb-4">or click to browse</p>
              <div className="flex justify-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  PDF
                </span>
                <span className="flex items-center gap-1">
                  <Music className="w-4 h-4" />
                  MusicXML
                </span>
                <span className="flex items-center gap-1">
                  <Music className="w-4 h-4" />
                  MIDI
                </span>
              </div>
              <input
                id="file-input"
                type="file"
                accept=".pdf,.xml,.musicxml,.mxl,.mid,.midi"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelect(f);
                }}
              />
            </div>
          )}

          {step === 'select' && (
            <label
              className="mt-4 flex items-start gap-2 cursor-pointer text-sm text-gray-300"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={isScan}
                onChange={(e) => setIsScan(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">This is a scanned photo</span>
                <span className="block text-gray-400">
                  Force scan-pipeline (skip vector classifier, use raster OMR + preprocessing).
                </span>
              </span>
            </label>
          )}

          {step === 'classifying' && (
            <div className="text-center py-8">
              <div className="loading-spinner mx-auto mb-4" />
              <p>Analyzing PDF...</p>
            </div>
          )}

          {step === 'confirm' && classification && (
            <div>
              <div className="mb-4">
                <h3 className="font-medium mb-2">PDF Analysis</h3>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Type:</span>
                      <span className="ml-2">
                        {classification.type === 'vector' ? 'Digital/Vector' : 'Scanned'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Quality:</span>
                      <span className="ml-2 capitalize">{classification.quality}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Pages:</span>
                      <span className="ml-2">{classification.pageCount}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Est. Accuracy:</span>
                      <span className="ml-2">
                        ~{Math.round(classification.estimatedAccuracy * 100)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {classification.estimatedAccuracy < 0.8 && (
                <div className="flex items-start gap-2 p-3 bg-yellow-900/30 border border-yellow-700 rounded-lg mb-4">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-yellow-400">Lower accuracy expected</p>
                    <p className="text-gray-300">
                      Some notes may need correction. For best results, upload a MusicXML file or a
                      higher-quality PDF.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={resetState}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUpload}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {step === 'uploading' && (
            <div className="text-center py-8">
              <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p>Processing... {Math.round(progress)}%</p>
            </div>
          )}

          {step === 'complete' && (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Music className="w-6 h-6" />
              </div>
              <p className="text-lg font-medium">Score ready!</p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
              <p>{error}</p>
              {errorTraceId && (
                <p className="mt-1 text-xs text-red-300 font-mono select-all">
                  trace: {errorTraceId}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
